import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, useToast } from "@ir/ui";
import {
  CreateMasterDataReasonCodeMappingDTO,
  DeleteAllMasterDataReasonCodeMappingsDTO,
  DeleteMasterDataReasonCodeMappingsDTO,
  UpdateMasterDataReasonCodeMappingDTO,
  type DeductionTypeLookupItem,
  type DeductionTypesLookupItem,
} from "@src/domain/dto/masterData";
import type { MasterDataReasonCodeMapping } from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import {
  CreateMasterDataReasonCodeMappingUseCase,
  DeleteAllMasterDataReasonCodeMappingsUseCase,
  DeleteMasterDataReasonCodeMappingsUseCase,
  GetDeductionTypesLookupUseCase,
  GetMasterDataPurchasersUseCase,
  GetPromoTypesLookupUseCase,
  UpdateMasterDataReasonCodeMappingUseCase,
} from "@src/application/useCases/masterData";
import {
  MasterDataDeductionTypeRepository,
  MasterDataPromoTypesLookupRepository,
  MasterDataPurchaserRepository,
  MasterDataReasonCodeMappingRepository,
} from "@src/infrastructure/repositories/masterData";
import {
  countActiveListFilters,
  ListFilterButton,
  ListFilterDrawer,
} from "@src/presentation/components/common/filters";
import { useUserPermissions } from "@src/presentation/hooks/permissions";
import { useMasterDataReasonCodeMappings } from "@src/presentation/providers/MasterDataReasonCodeMappingsDataProvider";
import { loadMasterDataPurchasersLookup } from "@src/presentation/providers/masterData/loadMasterDataPurchasersLookup";
import {
  getMasterDataDeductionTypesLookupCache,
  getMasterDataPromoTypesLookupCache,
  getMasterDataPurchasersLookupCache,
  invalidateMasterDataListCacheScopes,
  setMasterDataDeductionTypesLookupCache,
  setMasterDataPromoTypesLookupCache,
} from "@src/presentation/providers/masterData/masterDataListCache";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import {
  MasterDataReasonCodeMappingAddEditForm,
  type DeductionTypeSelectGroup,
  type PromoTypeSelectOption,
  type PurchaserSelectOption,
  type ReasonCodeMappingAddEditFormValues,
} from "../utils/MasterDataReasonCodeMappingAddEditForm";
import {
  MasterDataReasonCodeMappingDeleteConfirmModal,
  MasterDataReasonCodeMappingDeleteToolbar,
  type MasterDataReasonCodeMappingDeleteMode,
} from "../utils/MasterDataReasonCodeMappingDeleteActions";
import {
  MasterDataReasonCodeMappingFilterFields,
  type ReasonCodeMappingFilterDraft,
} from "../utils/MasterDataReasonCodeMappingFilterFields";
import { MasterDataReasonCodeMappingsTable } from "../utils/MasterDataReasonCodeMappingsTable";
import { MasterDataWritableListLayout } from "../utils/MasterDataWritableListLayout";
import { getMasterDataDeleteErrorMessage } from "../utils/deductionTypeForm.shared";
import { getMasterDataSaveErrorMessage } from "../utils/masterDataSaveErrors";
import {
  isReasonCodeKeyDuplicateError,
  REASON_CODE_KEY_MUST_BE_UNIQUE_MESSAGE,
} from "../utils/reasonCodeKeyValidation";
import {
  formatMasterDataRecordCount,
  useMasterDataAddEditModal,
} from "../utils/useMasterDataAddEditModal";

const EMPTY_FILTER_DRAFT: ReasonCodeMappingFilterDraft = {
  purchaserId: null,
  deductionTypeId: null,
  promoTypeId: null,
};

const EMPTY_FORM_VALUES: ReasonCodeMappingAddEditFormValues = {
  purchaserId: null,
  deductionTypeId: null,
  reasonCode: "",
  reasonCodeKey: "",
  promoTypeId: null,
};

function toDeductionTypeSelectSubtype(
  subtype: DeductionTypeLookupItem,
): DeductionTypeSelectGroup["subtypes"][number] | null {
  if (subtype.id == null) return null;
  return {
    typeId: subtype.id,
    typeName: subtype.deductionName ?? "",
  };
}

function toDeductionTypeSelectGroup(
  item: DeductionTypesLookupItem,
): DeductionTypeSelectGroup {
  const subtypes: DeductionTypeSelectGroup["subtypes"] = [];
  for (const subtype of item.subtypes ?? []) {
    const mapped = toDeductionTypeSelectSubtype(subtype);
    if (mapped) subtypes.push(mapped);
  }
  return {
    typeId: item.id ?? 0,
    typeName: item.deductionName ?? "",
    subtypes,
  };
}

const inflightDeductionTypesByBrand = new Map<
  string,
  Promise<DeductionTypeSelectGroup[]>
>();

function loadDeductionTypesLookup(
  brandKey: string,
  getDeductionTypesLookupUseCase: GetDeductionTypesLookupUseCase,
): Promise<DeductionTypeSelectGroup[]> {
  const cached =
    getMasterDataDeductionTypesLookupCache<DeductionTypeSelectGroup>(brandKey);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inflight = inflightDeductionTypesByBrand.get(brandKey);
  if (inflight) {
    return inflight;
  }

  const request = getDeductionTypesLookupUseCase
    .execute()
    .then((result) => {
      const groups = result.items.map(toDeductionTypeSelectGroup);
      setMasterDataDeductionTypesLookupCache(brandKey, groups);
      return groups;
    })
    .finally(() => {
      inflightDeductionTypesByBrand.delete(brandKey);
    });

  inflightDeductionTypesByBrand.set(brandKey, request);
  return request;
}

const inflightPromoTypesByBrand = new Map<string, Promise<PromoTypeSelectOption[]>>();

function loadPromoTypesLookup(
  brandKey: string,
  getPromoTypesLookupUseCase: GetPromoTypesLookupUseCase,
): Promise<PromoTypeSelectOption[]> {
  const cached = getMasterDataPromoTypesLookupCache<PromoTypeSelectOption>(brandKey);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inflight = inflightPromoTypesByBrand.get(brandKey);
  if (inflight) {
    return inflight;
  }

  const request = getPromoTypesLookupUseCase
    .execute()
    .then((result) => {
      const options = result.items.map((item) => ({
        promoTypeId: item.promoTypeId,
        promoTypeName: item.promoTypeName,
      }));
      setMasterDataPromoTypesLookupCache(brandKey, options);
      return options;
    })
    .finally(() => {
      inflightPromoTypesByBrand.delete(brandKey);
    });

  inflightPromoTypesByBrand.set(brandKey, request);
  return request;
}

export const MasterDataReasonCodeMappingsPage = () => {
  const {
    brandKey,
    reasonCodeMappings,
    loading,
    search,
    setSearch,
    filters,
    setFilters,
    total,
    page,
    setPage,
    refetch,
  } = useMasterDataReasonCodeMappings();
  const { success: successToast, error: errorToast } = useToast();
  const { hasAnyPermission } = useUserPermissions();
  const [form] = Form.useForm<ReasonCodeMappingAddEditFormValues>();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteMode, setDeleteMode] =
    useState<MasterDataReasonCodeMappingDeleteMode | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterDraft, setFilterDraft] =
    useState<ReasonCodeMappingFilterDraft>(EMPTY_FILTER_DRAFT);
  const [purchaserOptions, setPurchaserOptions] = useState<
    PurchaserSelectOption[]
  >([]);
  const [purchasersLoading, setPurchasersLoading] = useState(false);
  const [deductionTypeGroups, setDeductionTypeGroups] = useState<
    DeductionTypeSelectGroup[]
  >([]);
  const [deductionTypesLoading, setDeductionTypesLoading] = useState(false);
  const [promoTypeOptions, setPromoTypeOptions] = useState<PromoTypeSelectOption[]>(
    [],
  );
  const [promoTypesLoading, setPromoTypesLoading] = useState(false);

  const canDelete = hasAnyPermission([Permissions.MASTER_DATA_DELETE]);
  const activeFilterCount = countActiveListFilters(filters);

  const {
    modalMode,
    editingEntity,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataReasonCodeMapping>();

  const repository = useMemo(
    () => new MasterDataReasonCodeMappingRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataReasonCodeMappingUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataReasonCodeMappingUseCase(repository),
    [repository],
  );
  const deleteUseCase = useMemo(
    () => new DeleteMasterDataReasonCodeMappingsUseCase(repository),
    [repository],
  );
  const deleteAllUseCase = useMemo(
    () => new DeleteAllMasterDataReasonCodeMappingsUseCase(repository),
    [repository],
  );
  const getPurchasersUseCase = useMemo(
    () => new GetMasterDataPurchasersUseCase(new MasterDataPurchaserRepository()),
    [],
  );
  const getDeductionTypesLookupUseCase = useMemo(
    () =>
      new GetDeductionTypesLookupUseCase(new MasterDataDeductionTypeRepository()),
    [],
  );
  const getPromoTypesLookupUseCase = useMemo(
    () => new GetPromoTypesLookupUseCase(new MasterDataPromoTypesLookupRepository()),
    [],
  );

  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, page, filters]);

  const ensurePurchasersForModal = useCallback(async () => {
    const cached =
      getMasterDataPurchasersLookupCache<PurchaserSelectOption>(brandKey);
    if (cached) {
      setPurchaserOptions(cached);
      return;
    }

    setPurchasersLoading(true);
    try {
      const options = await loadMasterDataPurchasersLookup(
        brandKey,
        getPurchasersUseCase,
      );
      setPurchaserOptions(options);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load purchasers";
      errorToast({ content: message });
      setPurchaserOptions([]);
    } finally {
      setPurchasersLoading(false);
    }
  }, [brandKey, errorToast, getPurchasersUseCase]);

  const ensureDeductionTypesForModal = useCallback(async () => {
    const cached =
      getMasterDataDeductionTypesLookupCache<DeductionTypeSelectGroup>(brandKey);
    if (cached) {
      setDeductionTypeGroups(cached);
      return;
    }

    setDeductionTypesLoading(true);
    try {
      const groups = await loadDeductionTypesLookup(
        brandKey,
        getDeductionTypesLookupUseCase,
      );
      setDeductionTypeGroups(groups);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load deduction types";
      errorToast({ content: message });
      setDeductionTypeGroups([]);
    } finally {
      setDeductionTypesLoading(false);
    }
  }, [brandKey, errorToast, getDeductionTypesLookupUseCase]);

  const ensurePromoTypesForModal = useCallback(async () => {
    const cached =
      getMasterDataPromoTypesLookupCache<PromoTypeSelectOption>(brandKey);
    if (cached) {
      setPromoTypeOptions(cached);
      return;
    }

    setPromoTypesLoading(true);
    try {
      const options = await loadPromoTypesLookup(
        brandKey,
        getPromoTypesLookupUseCase,
      );
      setPromoTypeOptions(options);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load promo types";
      errorToast({ content: message });
      setPromoTypeOptions([]);
    } finally {
      setPromoTypesLoading(false);
    }
  }, [brandKey, errorToast, getPromoTypesLookupUseCase]);

  useEffect(() => {
    if (modalMode == null) return;
    void ensurePurchasersForModal();
    void ensureDeductionTypesForModal();
    void ensurePromoTypesForModal();
  }, [
    ensureDeductionTypesForModal,
    ensurePromoTypesForModal,
    ensurePurchasersForModal,
    modalMode,
  ]);

  useEffect(() => {
    if (!filterDrawerOpen) return;
    void ensurePurchasersForModal();
    void ensureDeductionTypesForModal();
    void ensurePromoTypesForModal();
  }, [
    ensureDeductionTypesForModal,
    ensurePromoTypesForModal,
    ensurePurchasersForModal,
    filterDrawerOpen,
  ]);

  const openFilterDrawer = useCallback(() => {
    setFilterDraft({ ...filters });
    setFilterDrawerOpen(true);
  }, [filters]);

  const closeFilterDrawer = useCallback(() => {
    setFilterDrawerOpen(false);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setFilters({ ...filterDraft });
    setFilterDrawerOpen(false);
  }, [filterDraft, setFilters]);

  const handleClearFilters = useCallback(() => {
    setFilterDraft(EMPTY_FILTER_DRAFT);
    setFilters(EMPTY_FILTER_DRAFT);
    setFilterDrawerOpen(false);
  }, [setFilters]);

  const initialValues = useMemo((): ReasonCodeMappingAddEditFormValues => {
    if (modalMode === "edit" && editingEntity) {
      return {
        purchaserId: editingEntity.purchaserId,
        deductionTypeId: editingEntity.deductionTypeId,
        reasonCode: editingEntity.reasonCode,
        reasonCodeKey: editingEntity.reasonCodeKey,
        promoTypeId: editingEntity.promoTypeId,
      };
    }
    return EMPTY_FORM_VALUES;
  }, [editingEntity, modalMode]);

  const currentDeductionTypeOption = useMemo(() => {
    if (modalMode !== "edit" || !editingEntity) return null;
    return {
      typeId: editingEntity.deductionTypeId,
      typeName:
        editingEntity.deductionTypeName ?? editingEntity.deductionTypeKey ?? "",
    };
  }, [editingEntity, modalMode]);

  const currentPromoTypeOption = useMemo(() => {
    if (modalMode !== "edit" || !editingEntity || editingEntity.promoTypeId == null) {
      return null;
    }
    return {
      promoTypeId: editingEntity.promoTypeId,
      promoTypeName: editingEntity.promoTypeName ?? "",
    };
  }, [editingEntity, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const refreshAfterWrite = useCallback(() => {
    invalidateMasterDataListCacheScopes(["reason-code-mappings"]);
    refetch();
  }, [refetch]);

  const handleSubmit = useCallback(
    async (values: ReasonCodeMappingAddEditFormValues) => {
      const purchaserId = values.purchaserId;
      const deductionTypeId = values.deductionTypeId;
      if (purchaserId == null || deductionTypeId == null) return;

      setIsSaving(true);
      try {
        if (modalMode === "edit" && editingEntity) {
          await updateUseCase.execute(
            new UpdateMasterDataReasonCodeMappingDTO({
              mappingId: editingEntity.mappingId,
              purchaserId,
              deductionTypeId,
              reasonCode: values.reasonCode.trim(),
              reasonCodeKey: editingEntity.reasonCodeKey,
              promoTypeId: values.promoTypeId ?? null,
            }),
          );
        } else {
          await createUseCase.execute(
            new CreateMasterDataReasonCodeMappingDTO({
              purchaserId,
              deductionTypeId,
              reasonCode: values.reasonCode.trim(),
              reasonCodeKey: values.reasonCodeKey.trim(),
              promoTypeId: values.promoTypeId ?? null,
            }),
          );
        }

        successToast({ content: "Reason code saved successfully" });
        closeModal();
        refreshAfterWrite();
      } catch (err) {
        if (isReasonCodeKeyDuplicateError(err)) {
          form.setFields([
            {
              name: "reasonCodeKey",
              errors: [REASON_CODE_KEY_MUST_BE_UNIQUE_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getMasterDataSaveErrorMessage(
              err,
              "Failed to save reason code. Please try again.",
              "Failed to save reason code. Please try again later.",
            ),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingEntity,
      errorToast,
      form,
      modalMode,
      refreshAfterWrite,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (deleteMode == null) return;
    setIsDeleting(true);
    try {
      const result =
        deleteMode === "all"
          ? await deleteAllUseCase.execute(
              new DeleteAllMasterDataReasonCodeMappingsDTO(),
            )
          : await deleteUseCase.execute(
              new DeleteMasterDataReasonCodeMappingsDTO({
                mappingIds: Array.from(selectedIds),
              }),
            );

      if (result.deletedCount === 1) {
        successToast({ content: "Reason Code deleted successfully." });
      } else if (result.deletedCount > 0) {
        successToast({
          content:
            result.message ??
            `Deleted ${result.deletedCount} reason codes.`,
        });
      }
      setSelectedIds(new Set());
      setDeleteMode(null);
      refreshAfterWrite();
    } catch (err) {
      errorToast({
        content: getMasterDataDeleteErrorMessage(
          err,
          "Failed to delete reason codes. Please try again.",
        ),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteAllUseCase,
    deleteMode,
    deleteUseCase,
    errorToast,
    refreshAfterWrite,
    selectedIds,
    successToast,
  ]);

  const selectedCount = selectedIds.size;
  const selectedReasonCodeKey =
    selectedCount === 1
      ? (reasonCodeMappings.find(
          (mapping) => mapping.mappingId === Array.from(selectedIds)[0],
        )?.reasonCodeKey ?? null)
      : null;

  return (
    <>
      <MasterDataWritableListLayout
        title="Reason Code"
        subtitle={formatMasterDataRecordCount(loading, total)}
        searchPlaceholder="Search reason codes"
        search={search}
        setSearch={setSearch}
        searchSection="reason-code"
        addButtonLabel="Add Reason Code"
        onAdd={openCreateModal}
        filterActions={
          <ListFilterButton
            onPress={openFilterDrawer}
            activeCount={activeFilterCount}
            className="ms-1"
          />
        }
        headerActions={
          <MasterDataReasonCodeMappingDeleteToolbar
            selectedCount={selectedCount}
            onDeleteSelected={() => setDeleteMode("selected")}
            onDeleteAll={() => setDeleteMode("all")}
            className="ms-2"
          />
        }
        hideSearch={selectedCount > 0}
        loading={loading}
      >
        <MasterDataReasonCodeMappingsTable
          reasonCodeMappings={reasonCodeMappings}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          onEdit={openEditModal}
          showSelection={canDelete}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </MasterDataWritableListLayout>

      <ListFilterDrawer
        open={filterDrawerOpen}
        title="Filters"
        onClose={closeFilterDrawer}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      >
        <MasterDataReasonCodeMappingFilterFields
          draft={filterDraft}
          onChange={setFilterDraft}
          purchaserOptions={purchaserOptions}
          purchasersLoading={purchasersLoading}
          deductionTypeGroups={deductionTypeGroups}
          deductionTypesLoading={deductionTypesLoading}
          promoTypeOptions={promoTypeOptions}
          promoTypesLoading={promoTypesLoading}
        />
      </ListFilterDrawer>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Reason Code"
        editTitle="Edit Reason Code"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataReasonCodeMappingAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          purchaserOptions={purchaserOptions}
          purchasersLoading={purchasersLoading}
          deductionTypeGroups={deductionTypeGroups}
          deductionTypesLoading={deductionTypesLoading}
          currentDeductionTypeOption={currentDeductionTypeOption}
          promoTypeOptions={promoTypeOptions}
          promoTypesLoading={promoTypesLoading}
          currentPromoTypeOption={currentPromoTypeOption}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <MasterDataReasonCodeMappingDeleteConfirmModal
        deleteMode={deleteMode}
        isDeleting={isDeleting}
        selectedCount={selectedCount}
        selectedReasonCodeKey={selectedReasonCodeKey}
        onClose={() => setDeleteMode(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
